import { Button, Spin, Tree, Typography } from "antd";
import type { DataNode } from "antd/es/tree";
import { useTranslation } from "react-i18next";
import GEmpty from "@/styles/Placeholder/GEmpty";
import { CommonStateEnum } from "@/types/common";
import { EquipmentCategory } from "@/types/equipment";
import { CategoryPanel, CategoryTreeBody } from "../../style";

// 设备类目树组件参数
interface CategoryTreeProps {
  treeData: EquipmentCategory[]; // 类目树数据
  selectedId?: string; // 当前选中的类目 ID
  expandedKeys: string[]; // 已展开的类目 ID
  loading: boolean; // 类目树加载状态
  onSelect: (id: string) => void; // 选择类目
  onExpand: (keys: string[]) => void; // 更新展开项
  onAddRoot: () => void; // 新增根类目
  canEdit: boolean; // 是否允许新增/编辑设备类目
}

// 展示设备类目层级并维护选择与展开状态
const CategoryTree = ({
  treeData,
  selectedId,
  expandedKeys,
  loading,
  onSelect,
  onExpand,
  onAddRoot,
  canEdit,
}: CategoryTreeProps) => {
  const { t } = useTranslation();

  return (
    <CategoryPanel>
      {canEdit && (
        <Button
          type="primary"
          block
          disabled={loading}
          onClick={onAddRoot}
        >
          {t("equipment.addRootCategory")}
        </Button>
      )}
      <CategoryTreeBody>
        {loading ? (
          <Spin />
        ) : treeData.length > 0 ? (
          <Tree
            blockNode
            showLine
            treeData={treeData as unknown as DataNode[]}
            fieldNames={{ title: "name", key: "id", children: "subs" }}
            selectedKeys={selectedId ? [selectedId] : []}
            expandedKeys={expandedKeys}
            onExpand={keys => onExpand(keys.map(String))}
            onSelect={keys => {
              const id = keys[0];
              if (id) onSelect(String(id));
            }}
            titleRender={node => {
              const category = node as unknown as EquipmentCategory;
              // 禁用类目仅弱化展示，仍允许选择并查看详情
              const disabled = category.state === CommonStateEnum.UN_ACTIVE;

              return (
                <Typography.Text type={disabled ? "secondary" : undefined}>
                  {category.name}
                </Typography.Text>
              );
            }}
          />
        ) : (
          <GEmpty description={t("equipment.noCategoryData")} />
        )}
      </CategoryTreeBody>
    </CategoryPanel>
  );
};

export default CategoryTree;
